import { useEffect, useRef } from 'react';

import { sendProductView } from '../utils/productTracking';

/*
  Measures how long a product is actively on screen and reports the total
  seconds to the backend when the dwell ends.

  A dwell ends when: the product changes, the component unmounts, the tab
  is hidden, or the page is closed. Time while the tab is hidden is NOT
  counted, so numbers reflect real attention.

  @param productId  the product being viewed (null pauses tracking)
  @param source     'details' | 'quickview'
  @param active     set false to pause (e.g. a closed modal)
*/
export function useProductDwell(productId, source = 'details', active = true) {
  const startRef = useRef(null);
  const accumRef = useRef(0);

  useEffect(() => {
    if (!productId || !active) {
      return undefined;
    }

    accumRef.current = 0;
    startRef.current = Date.now();

    const stop = () => {
      if (startRef.current != null) {
        accumRef.current += (Date.now() - startRef.current) / 1000;
        startRef.current = null;
      }
    };

    const resume = () => {
      if (startRef.current == null) {
        startRef.current = Date.now();
      }
    };

    const flush = () => {
      stop();
      const seconds = accumRef.current;
      accumRef.current = 0;
      if (seconds >= 1) {
        sendProductView({
          productId,
          duration: seconds,
          source
        });
      }
    };

    const onVisibility = () => {
      if (document.hidden) {
        flush();
      } else {
        resume();
      }
    };

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', flush);

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', flush);
      flush();
    };
  }, [productId, source, active]);
}
